import { CalendarIcon, CircleAlert, ClipboardList } from "lucide-react"
import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { useAuth } from "@/context/AuthContext"
import { createLeaveRequest } from "@/lib/collections/requestsLeave/user/addRequestsLeave"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Calendar } from "@/components/ui/calendar"
import { format } from "date-fns"
import { es } from "date-fns/locale"

const AddRequestLeave = ({ onSuccess }: { onSuccess?: () => void }) => {
    const { user } = useAuth()
    const [description, setDescription] = useState("");
    const [startTime, setStartTime] = useState<string>("");
    const [endTime, setEndTime] = useState<string>("");
    const [dateStart, setStartDate] = useState<Date | undefined>(undefined);
    const [endDate, setEndDate] = useState<Date | undefined>(undefined);
    const [openStartDate, setOpenStartDate] = useState(false);
    const [openEndDate, setOpenEndDate] = useState(false);
    const [allDay, setAllDay] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [title, setTitle] = useState("");

    const [generalError, setGeneralError] = useState<string>("");
    const [fieldErrors, setFieldErrors] = useState<{
        title?: string;
        dateStart?: string;
        startTime?: string;
        endDate?: string;
        endTime?: string;
        description?: string;
    }>({});

    useEffect(() => {
        setStartDate(undefined);
        setEndDate(undefined);
        setStartTime("");
        setEndTime("");
    }, [allDay]);

    const clearErrors = () => {
        setGeneralError("");
        setFieldErrors({});
    };

    const handleSaveEvent = async () => {
        if (isSubmitting) return;
        clearErrors();
        let loadingToast: ReturnType<typeof toast.loading> | null = null;

        try {
            if (!user?.$id) {
                setGeneralError("Usuario no autenticado. Por favor recarga la página.");
                return;
            }

            const errors: typeof fieldErrors = {};

            if (!title.trim()) errors.title = "El título es obligatorio";
            if (!dateStart) errors.dateStart = "La fecha de inicio es obligatoria";
            if (!description.trim()) errors.description = "El comentario es obligatorio";

            if (!allDay) {
                if (!startTime) errors.startTime = "La hora de inicio es obligatoria";
                if (!endTime) errors.endTime = "La hora final es obligatoria";
            }

            if (Object.keys(errors).length > 0) {
                setFieldErrors(errors);
                return;
            }

            const ecuadorNow = new Date();
            const startDateOnly = new Date(dateStart!.getFullYear(), dateStart!.getMonth(), dateStart!.getDate());
            const nowDateOnly = new Date(ecuadorNow.getFullYear(), ecuadorNow.getMonth(), ecuadorNow.getDate());

            if (startDateOnly < nowDateOnly) {
                setFieldErrors({ dateStart: "No puedes solicitar permisos para fechas pasadas" });
                return;
            }



            if (allDay && endDate) {
                const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
                if (endDateOnly < startDateOnly) {
                    setFieldErrors({ endDate: "La fecha final no puede ser anterior a la fecha inicial" });
                    return;
                }
            }

            if (!allDay && startTime && endTime) {
                const finalStart = combineDateAndTime(dateStart!, startTime);
                const finalEnd = combineDateAndTime(dateStart!, endTime);

                if (finalEnd <= finalStart) {
                    setFieldErrors({ endTime: "La hora final debe ser posterior a la hora de inicio" });
                    return;
                }

                // Recién aquí validar si la hora de inicio ya pasó (solo si es hoy)
                if (startDateOnly.getTime() === nowDateOnly.getTime()) {
                    if (finalStart <= ecuadorNow) {
                        const currentTimeStr = ecuadorNow.toLocaleTimeString('es-EC', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                        });
                        setFieldErrors({ startTime: `La hora de inicio debe ser posterior a la hora actual (${currentTimeStr})` });
                        return;
                    }
                }
            }
            setIsSubmitting(true);
            loadingToast = toast.loading("Guardando solicitud...");

            let finalStartDate = new Date(dateStart!);
            let finalEndDate: Date;

            if (allDay) {
                finalStartDate.setHours(8, 0, 0, 0);
                finalEndDate = new Date(endDate ?? dateStart!);
                finalEndDate.setHours(17, 0, 0, 0);
            } else {
                finalStartDate = combineDateAndTime(dateStart!, startTime);
                finalEndDate = combineDateAndTime(dateStart!, endTime);
            }

            await createLeaveRequest({
                userId: user.$id,
                startDate: finalStartDate,
                endDate: finalEndDate,
                allDay,
                comment: description.trim(),
                title: title.trim()
            });

            toast.dismiss(loadingToast);
            setDescription("");
            setTitle("");
            setAllDay(false);
            setStartDate(undefined);
            setEndDate(undefined);
            setIsDialogOpen(false);
            setStartTime("");
            setEndTime("");
            clearErrors();
            onSuccess?.();

            toast.success(`Solicitud creada exitosamente.`);

        } catch (error: any) {
            if (loadingToast !== null) toast.dismiss(loadingToast);
            setGeneralError(error?.message || "Error al crear la solicitud. Intenta de nuevo.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const combineDateAndTime = (date: Date, time: string): Date => {
        const [hours, minutes] = time.split(":").map(Number);
        const combined = new Date(date);
        combined.setHours(hours, minutes, 0, 0);
        return combined;
    };

    return (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
                <Button className="cursor-pointer" variant="default">
                    <ClipboardList />
                    Registrar solicitud
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-106.25 font-outfit max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Registro de solicitud de permiso</DialogTitle>
                    <DialogDescription>
                        Completa la información para enviar tu solicitud de permiso.
                    </DialogDescription>

                    {generalError && (
                        <div className="flex items-center gap-2 rounded-md border border-red-300 bg-red-50 dark:bg-red-950 dark:border-red-800 px-3 py-2 text-sm text-red-600 dark:text-red-400">
                            <CircleAlert className="h-4 w-4 shrink-0" />
                            <span>{generalError}</span>
                        </div>
                    )}
                </DialogHeader>

                <div className="grid gap-3">
                    <Label htmlFor="title">Título</Label>
                    <Input
                        id="title"
                        name="title"
                        value={title}
                        onChange={(e) => {
                            setTitle(e.target.value);
                            if (fieldErrors.title) setFieldErrors(prev => ({ ...prev, title: undefined }));
                        }}
                        className={fieldErrors.title ? "border-red-500 focus-visible:ring-red-500" : ""}
                        placeholder="Ej: Cita médica, Asunto personal..."
                        maxLength={100}
                    />
                    {fieldErrors.title && (
                        <p className="text-xs text-red-500">{fieldErrors.title}</p>
                    )}
                </div>

                <div className="grid gap-4">
                    <div className="flex items-center space-x-2">
                        <Switch
                            id="allDay"
                            checked={allDay}
                            onCheckedChange={setAllDay}
                        />
                        <Label htmlFor="allDay">¿Día completo?</Label>
                    </div>

                    <div className="grid gap-3">
                        <Label>{allDay ? "Desde" : "Fecha de inicio"}</Label>
                        <Popover open={openStartDate} onOpenChange={setOpenStartDate}>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className={`w-full justify-start text-left font-normal ${fieldErrors.dateStart ? "border-red-500 text-red-500" : ""}`}
                                >
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {dateStart ? format(dateStart, "PPP", { locale: es }) : "Selecciona una fecha"}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                    mode="single"
                                    selected={dateStart}
                                    captionLayout="dropdown"
                                    disabled={(date) => {
                                        const day = date.getDay();
                                        const isWeekend = day === 0 || day === 6;
                                        const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
                                        return isWeekend || isPast;
                                    }}
                                    onSelect={(date) => {
                                        setStartDate(date);
                                        if (allDay && date && (!endDate || endDate < date)) setEndDate(date);
                                        setOpenStartDate(false);
                                        if (fieldErrors.dateStart) setFieldErrors(prev => ({ ...prev, dateStart: undefined }));
                                    }}
                                />
                            </PopoverContent>
                        </Popover>
                        {fieldErrors.dateStart && (
                            <p className="text-xs text-red-500">{fieldErrors.dateStart}</p>
                        )}

                        {!allDay && (
                            <Input
                                type="time"
                                value={startTime}
                                onChange={(e) => {
                                    setStartTime(e.target.value);
                                    if (fieldErrors.startTime) setFieldErrors(prev => ({ ...prev, startTime: undefined }));
                                }}
                                className={`[&::-webkit-calendar-picker-indicator]:brightness-0 dark:[&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer${fieldErrors.startTime ? " border-red-500 focus-visible:ring-red-500" : ""}`}
                            />
                        )}
                        {fieldErrors.startTime && (
                            <p className="text-xs text-red-500">{fieldErrors.startTime}</p>
                        )}

                        {allDay && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Se registrará de 8:00 AM a 5:00 PM cada día laborable.
                            </p>
                        )}
                    </div>

                    {allDay && (
                    <div className="grid gap-3">
                            <Label>Hasta</Label>
                            <Popover open={openEndDate} onOpenChange={setOpenEndDate}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className={`w-full justify-start text-left font-normal ${fieldErrors.endDate ? "border-red-500 text-red-500" : ""}`}
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {endDate ? format(endDate, "PPP", { locale: es }) : "Selecciona una fecha"}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={endDate}
                                        captionLayout="dropdown"
                                        disabled={(date) => {
                                            const day = date.getDay();
                                            const isWeekend = day === 0 || day === 6;
                                            const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
                                            const isBeforeStart = !!dateStart && date < new Date(new Date(dateStart).setHours(0, 0, 0, 0));
                                            return isWeekend || isPast || isBeforeStart;
                                        }}
                                        onSelect={(date) => {
                                            setEndDate(date);
                                            setOpenEndDate(false);
                                            if (fieldErrors.endDate) setFieldErrors(prev => ({ ...prev, endDate: undefined }));
                                        }}
                                    />
                                </PopoverContent>
                            </Popover>
                            {fieldErrors.endDate && (
                                <p className="text-xs text-red-500">{fieldErrors.endDate}</p>
                            )}
                    </div>
                    )}

                    {!allDay && (
                        <div className="grid gap-3">
                            <Label>Hora de fin</Label>
                            <Input
                                type="time"
                                value={endTime}
                                onChange={(e) => {
                                    setEndTime(e.target.value);
                                    if (fieldErrors.endTime) setFieldErrors(prev => ({ ...prev, endTime: undefined }));
                                }}
                                className={`[&::-webkit-calendar-picker-indicator]:brightness-0 dark:[&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer${fieldErrors.endTime ? " border-red-500 focus-visible:ring-red-500" : ""}`}
                            />
                            {fieldErrors.endTime && (
                                <p className="text-xs text-red-500">{fieldErrors.endTime}</p>
                            )}
                        </div>
                    )}

                    <div className="grid gap-3">
                        <Label htmlFor="description">Comentario:</Label>
                        <Textarea
                            id="description"
                            name="description"
                            value={description}
                            onChange={(e) => {
                                setDescription(e.target.value);
                                if (fieldErrors.description) setFieldErrors(prev => ({ ...prev, description: undefined }));
                            }}
                            className={fieldErrors.description ? "border-red-500 focus-visible:ring-red-500" : ""}
                            placeholder="Describe el motivo de tu solicitud..."
                            maxLength={500}
                        />
                        {fieldErrors.description && (
                            <p className="text-xs text-red-500">{fieldErrors.description}</p>
                        )}
                        <span className="text-xs text-gray-500">
                            {description.length}/500 caracteres
                        </span>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        onClick={handleSaveEvent}
                        disabled={isSubmitting}
                        className="cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? "Guardando..." : "Guardar"}
                    </Button>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            className="cursor-pointer"
                            variant="outline"
                            disabled={isSubmitting}
                        >
                            Cancelar
                        </Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

export default AddRequestLeave;